import { memo, useState, useEffect, useRef } from 'preact/compat';
import { useAppDispatch, useAppSelector } from '../hooks';
import { useColumnResize, useCopyCell, useCopyRow } from '../hooks';
import { useVirtualScroll } from '../hooks/useVirtualScroll';
import { fetchBuyerSelections, saveBuyerSelections, finishBuyer, changeRowWareGroup } from '../store/offersSlice';
import ColumnFilter from './ColumnFilter';
import ColumnVisibilityModal from './ColumnVisibilityModal';
import LoadingOverlay from './LoadingOverlay';
import ConfirmModal from './ConfirmModal';
import BuyerSelectHistoryTooltip from './BuyerSelectHistoryTooltip';
import VendorBackgroundTooltip from './VendorBackgroundTooltip';
import LazySelect from './LazySelect';
import { api } from '../api/client';
import type { IBuyerSelection } from '../types';
import { copyToClipboard } from '../utils/clipboard';
import type { SelectionField } from '../constants/columns';
import {
    SELECTION_FIELDS, SELECTION_LABELS, SELECTION_INITIAL_WIDTHS,
    SELECTION_NUMBER_FIELDS,
} from '../constants/columns';

interface Props {
    offerCode: number;
    buyerCode?: number;
    buyerCodes?: number[];
    buyerName: string;
    onBack: () => void;
}

const formatNum = (v: any) => {
    const n = Number(v);
    return isNaN(n) ? String(v ?? '') : Number.isInteger(n) ? String(n) : n.toFixed(2);
};

// Map coordinator fields to IOfferRow keys
const COORD_FIELD_MAP: Partial<Record<SelectionField, string>> = {
    num_row: 'num_row', coord_status: 'status_name', ware_group: 'ware_category_name', season_name: 'season_name',
    coord_quantity: 'quantity', coord_purchase_price: 'purchase_price',
    vendor_selling_price: 'vendor_selling_price', wholesale_price: 'wholesale_price', vendor_nds: 'vendor_nds',
    vendor_ware: 'vendor_ware', vendore_code: 'vendore_code', vendore_color_code: 'vendore_color_code',
    vendor_color: 'vendor_color', vendor_size: 'vendor_size', vendor_description: 'vendor_description',
    vendor_brand: 'vendor_brand', vendor_country_manufactory: 'vendor_country_manufactory',
    vendor_gendor: 'vendor_gendor', vendor_season: 'vendor_season', vendor_composition: 'vendor_composition',
    vendor_age_group: 'vendor_age_group', box_num: 'box_num', barcode: 'barcode',
    date_expiration: 'date_expiration', date_production: 'date_production', foto_url: 'foto_url',
};

const getCellValue = (sel: IBuyerSelection, field: SelectionField, editedRows: Record<number, Partial<IBuyerSelection>>): any => {
    const coord = sel.coordinator;
    const edit = editedRows[sel.code];

    // Selection editable fields
    if (field === 'sel_quantity') { const v = edit && 'quantity' in edit ? edit.quantity : sel.quantity; return v != null ? formatNum(v) : ''; }
    if (field === 'sel_purchase_price') { const v = edit && 'purchase_price' in edit ? edit.purchase_price : sel.purchase_price; return v != null ? formatNum(v) : ''; }
    if (field === 'sel_selling_price') { const v = edit && 'selling_price' in edit ? edit.selling_price : sel.selling_price; return v != null ? formatNum(v) : ''; }
    if (field === 'sel_recommended_price') { const v = edit && 'recommended_price' in edit ? edit.recommended_price : sel.recommended_price; return v != null ? formatNum(v) : ''; }
    if (field === 'sel_status') return sel.status_name || '';
    if (field === 'history') return (sel.have_hst ?? 0) > 0 ? 'Да' : '';

    // Coordinator read-only fields
    const coordKey = COORD_FIELD_MAP[field];
    if (coordKey && coord) {
        const v = (coord as any)[coordKey];
        if (v == null) return '';
        if (SELECTION_NUMBER_FIELDS.includes(field)) return formatNum(v);
        return String(v);
    }
    return '';
};

const SelectionDetailPage = memo(({ offerCode, buyerCode, buyerCodes, buyerName, onBack }: Props) => {
    const allBuyerCodes = buyerCodes ?? (buyerCode ? [buyerCode] : []);
    const dispatch = useAppDispatch();
    const selections = useAppSelector(s => s.offers.selections);
    const loadingSel = useAppSelector(s => s.offers.loadingSelections);
    const loading = useAppSelector(s => s.offers.loading);
    const refs = useAppSelector(s => s.references.data);
    const offer = useAppSelector(s => s.offers.items.find(o => Number(o.code) === offerCode));

    const [editedRows, setEditedRows] = useState<Record<number, Partial<IBuyerSelection>>>({});
    const [searchText, setSearchText] = useState('');
    const [showUnprocessed, setShowUnprocessed] = useState(false);
    const [srPopup, setSrPopup] = useState<{ title: string; data: any[] | null; loading: boolean } | null>(null);
    const [confirmAction, setConfirmAction] = useState<{ message: string; action: () => void } | null>(null);

    // Column infrastructure
    const [isColumnVisibilityModalOpen, setIsColumnVisibilityModalOpen] = useState(false);
    const [columnFilters, setColumnFilters] = useState<Record<string, Set<any>>>({});
    const [openFilterField, setOpenFilterField] = useState<SelectionField | null>(null);
    const [sortConfig, setSortConfig] = useState<{ field: SelectionField | null; direction: 'asc' | 'desc' }>({ field: null, direction: 'asc' });
    const [activeCell, setActiveCell] = useState<{ rowIndex: number; colIndex: number } | null>(null);
    const [isResizing, setIsResizing] = useState(false);
    const [draggedColumn, setDraggedColumn] = useState<string | null>(null);
    const [dragOverColumn, setDragOverColumn] = useState<string | null>(null);

    const storageKey = `visibleColumns_sel_${offerCode}_${buyerCode}`;
    const [visibleColumns, setVisibleColumns] = useState<Set<string>>(() => {
        try { const s = localStorage.getItem(storageKey); if (s) return new Set(JSON.parse(s)); } catch {}
        return new Set(SELECTION_FIELDS as string[]);
    });
    const [columnOrder, setColumnOrder] = useState<SelectionField[]>(() => {
        try {
            const s = localStorage.getItem(`columnOrder_sel_${offerCode}_${buyerCode}`);
            if (s) {
                const parsed: string[] = JSON.parse(s);
                const valid = parsed.filter(c => SELECTION_FIELDS.includes(c as SelectionField)) as SelectionField[];
                const missing = SELECTION_FIELDS.filter(c => !valid.includes(c));
                return [...valid, ...missing];
            }
        } catch {}
        return [...SELECTION_FIELDS];
    });

    useEffect(() => {
        try { localStorage.setItem(storageKey, JSON.stringify(Array.from(visibleColumns))); } catch {}
    }, [visibleColumns, storageKey]);

    useEffect(() => {
        try { localStorage.setItem(`columnOrder_sel_${offerCode}_${buyerCode}`, JSON.stringify(columnOrder)); } catch {}
    }, [columnOrder, offerCode, buyerCode]);

    const filteredColumns = columnOrder.filter(col => visibleColumns.has(col));

    const { headerProps, resizeHandleProps } = useColumnResize(
        SELECTION_INITIAL_WIDTHS as Record<string, number>,
        `columnWidths_sel_${offerCode}_${buyerCode}`
    );
    const { handleCellClick, getCellClass, clearSelection: clearCellSelection } = useCopyCell();
    const { handleRowClick, getRowClass, clearSelection: clearRowSelection, selectedRowId } = useCopyRow();
    const tableScrollRef = useRef<HTMLDivElement>(null);

    const wareCategories = [...(refs?.ware_categories ?? [])].sort((a, b) => (a.name || '').localeCompare(b.name || '', 'ru'));
    const selectStatuses = refs?.buyer_select_statuses ?? [];

    useEffect(() => {
        if (allBuyerCodes.length > 0) {
            dispatch(fetchBuyerSelections({ code: offerCode, buyerCodes: allBuyerCodes }));
        }
    }, [dispatch, offerCode, allBuyerCodes.join(',')]);

    // ── SR popups ──
    const showSrArticle = async (vendorWare: string) => {
        setSrPopup({ title: `SR по артикулу: ${vendorWare}`, data: null, loading: true });
        try { const d = await api.getSrArticle(vendorWare); setSrPopup(p => p ? { ...p, data: d, loading: false } : null); }
        catch { setSrPopup(p => p ? { ...p, data: [], loading: false } : null); }
    };
    const showSrBrandWareGroup = async (brand: string, wareGroup: string) => {
        setSrPopup({ title: `SR: ${brand} / ${wareGroup}`, data: null, loading: true });
        try { const d = await api.getSrBrandWareGroup(brand, wareGroup); setSrPopup(p => p ? { ...p, data: d, loading: false } : null); }
        catch { setSrPopup(p => p ? { ...p, data: [], loading: false } : null); }
    };

    // ── Editing helpers ──
    const getEdited = (sel: IBuyerSelection, field: 'quantity' | 'purchase_price' | 'selling_price' | 'recommended_price' | 'status_code'): number | null => {
        const edit = editedRows[sel.code];
        if (edit && field in edit) return (edit as any)[field];
        return sel[field] as number | null;
    };
    const setFieldValue = (code: number, field: string, value: any) => {
        setEditedRows(prev => ({ ...prev, [code]: { ...prev[code], [field]: value } }));
    };

    // ── Filter & Sort ──
    let displaySelections = [...selections];
    if (showUnprocessed) {
        displaySelections = displaySelections.filter(s => s.status_name === 'Новая' || s.status_name === 'Обновлено');
    }
    if (searchText) {
        const q = searchText.toLowerCase();
        displaySelections = displaySelections.filter(s =>
            String(s.purchase_price ?? '').includes(q) ||
            String(s.quantity ?? '').includes(q) ||
            (s.coordinator?.ware_category_name || '').toLowerCase().includes(q) ||
            (s.coordinator?.vendor_ware || '').toLowerCase().includes(q) ||
            (s.coordinator?.vendor_brand || '').toLowerCase().includes(q)
        );
    }

    // Column filters
    displaySelections = displaySelections.filter(sel =>
        filteredColumns.every(field => {
            const filter = columnFilters[field];
            if (!filter || filter.size === 0) return true;
            const value = String(getCellValue(sel, field, {})).trim();
            return filter.has(value === '' ? '(пусто)' : value);
        })
    );

    // Sort
    const sortedSelections = displaySelections.sort((a, b) => {
        if (!sortConfig.field) {
            // Default: TG then num_row
            const aTG = a.coordinator?.ware_category_name || '';
            const bTG = b.coordinator?.ware_category_name || '';
            const tgCmp = aTG.localeCompare(bTG, 'ru');
            if (tgCmp !== 0) return tgCmp;
            return (a.coordinator?.num_row ?? 0) - (b.coordinator?.num_row ?? 0);
        }
        const field = sortConfig.field;
        const aVal = getCellValue(a, field, editedRows);
        const bVal = getCellValue(b, field, editedRows);
        const isNum = SELECTION_NUMBER_FIELDS.includes(field);
        let cmp = 0;
        if (isNum) {
            cmp = (parseFloat(aVal) || 0) - (parseFloat(bVal) || 0);
        } else {
            cmp = String(aVal).localeCompare(String(bVal), 'ru');
        }
        return sortConfig.direction === 'asc' ? cmp : -cmp;
    });

    const handleFilterChange = (field: string, values: Set<any>) => setColumnFilters(prev => ({ ...prev, [field]: values }));
    const handleSortChange = (field: string, dir: 'asc' | 'desc') => setSortConfig({ field: field as SelectionField, direction: dir });

    // ── Drag reorder ──
    const handleColumnDragStart = (e: DragEvent, column: SelectionField) => {
        if (isResizing) { e.preventDefault(); return; }
        if ((e.target as HTMLElement).closest('[data-resize-handle]')) { e.preventDefault(); return; }
        setDraggedColumn(column);
        if (e.dataTransfer) { e.dataTransfer.effectAllowed = 'move'; e.dataTransfer.setData('text/plain', column); }
    };
    const handleColumnDragEnd = () => { setDraggedColumn(null); setDragOverColumn(null); };
    const handleColumnDragOver = (e: DragEvent, column: SelectionField) => {
        e.preventDefault();
        if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
        if (draggedColumn && draggedColumn !== column) setDragOverColumn(column);
    };
    const handleColumnDragLeave = () => setDragOverColumn(null);
    const handleColumnDrop = (e: DragEvent, targetColumn: SelectionField) => {
        e.preventDefault();
        if (!draggedColumn || draggedColumn === targetColumn) { setDraggedColumn(null); setDragOverColumn(null); return; }
        const newOrder = [...columnOrder];
        const dragIdx = newOrder.indexOf(draggedColumn as SelectionField);
        const targetIdx = newOrder.indexOf(targetColumn);
        if (dragIdx === -1 || targetIdx === -1) { setDraggedColumn(null); setDragOverColumn(null); return; }
        newOrder.splice(dragIdx, 1);
        newOrder.splice(targetIdx, 0, draggedColumn as SelectionField);
        setColumnOrder(newOrder);
        setDraggedColumn(null); setDragOverColumn(null);
    };

    // ── Save & Finish ──
    const handleSave = async () => {
        const rowsToSave = selections.map(sel => {
            const edit = editedRows[sel.code] || {};
            return {
                code: sel.code,
                offer_buyer_code: sel.offer_buyer_code,
                offer_coordinator_code: sel.offer_coordinator_code,
                status_code: edit.status_code ?? sel.status_code,
                quantity: edit.quantity ?? sel.quantity,
                purchase_price: edit.purchase_price ?? sel.purchase_price,
                selling_price: edit.selling_price ?? sel.selling_price,
                recommended_price: edit.recommended_price ?? sel.recommended_price,
            };
        });
        // Group by offer_buyer_code and save each group
        const groups: Record<number, any[]> = {};
        for (const r of rowsToSave) {
            (groups[r.offer_buyer_code] ??= []).push(r);
        }
        const results = await Promise.all(
            Object.entries(groups).map(([bc, rows]) =>
                dispatch(saveBuyerSelections({ code: offerCode, buyerCode: Number(bc), rows }))
            )
        );
        if (results.every(r => saveBuyerSelections.fulfilled.match(r))) {
            setEditedRows({});
            dispatch(fetchBuyerSelections({ code: offerCode, buyerCodes: allBuyerCodes }));
        }
    };

    const handleFinish = () => {
        setConfirmAction({
            message: 'Завершить отборку? Строки без количества получат 0, все статусы \u2192 "Обработано".',
            action: async () => {
                const PROCESSED_STATUS = 4;
                const rowsToSave = selections.map(sel => {
                    const edit = editedRows[sel.code] || {};
                    return {
                        code: sel.code,
                        offer_buyer_code: sel.offer_buyer_code,
                        offer_coordinator_code: sel.offer_coordinator_code,
                        status_code: PROCESSED_STATUS,
                        quantity: (edit.quantity ?? sel.quantity) || 0,
                        purchase_price: edit.purchase_price ?? sel.purchase_price,
                        selling_price: edit.selling_price ?? sel.selling_price,
                        recommended_price: edit.recommended_price ?? sel.recommended_price,
                    };
                });
                const groups: Record<number, any[]> = {};
                for (const r of rowsToSave) {
                    (groups[r.offer_buyer_code] ??= []).push(r);
                }
                await Promise.all(
                    Object.entries(groups).map(([bc, rows]) =>
                        dispatch(saveBuyerSelections({ code: offerCode, buyerCode: Number(bc), rows }))
                    )
                );
                setEditedRows({});
                await Promise.all(
                    allBuyerCodes.map(bc => dispatch(finishBuyer({ code: offerCode, buyerCode: bc })))
                );
                dispatch(fetchBuyerSelections({ code: offerCode, buyerCodes: allBuyerCodes }));
                setConfirmAction(null);
            },
        });
    };

    // ── Virtual scroll ──
    const ROW_HEIGHT = 36;
    const { containerRef: virtualContainerRef, startIndex, endIndex, totalHeight, offsetY } = useVirtualScroll({
        totalItems: sortedSelections.length,
        itemHeight: ROW_HEIGHT,
        overscan: 15,
    });
    const visibleItems = sortedSelections.slice(startIndex, endIndex);

    const hasEdits = Object.keys(editedRows).length > 0;

    return (
        <div className="h-full flex flex-col relative">
            <LoadingOverlay visible={loadingSel || loading} />

            {/* Header */}
            <div className="px-6 py-3 border-b border-base-300 bg-base-100 flex items-center gap-3 flex-wrap">
                <button className="btn btn-sm btn-ghost" onClick={onBack}>
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                    </svg>
                    Назад
                </button>
                <h3 className="font-semibold">{buyerName} — Отборка</h3>

                {offer?.vendor_code && <VendorBackgroundTooltip vendorCode={Number(offer.vendor_code)} vendorName={offer.vendor_name || 'Поставщик'} />}

                <div className="flex-1" />

                <input type="text" placeholder="Поиск..." className="input input-bordered input-sm w-40"
                    value={searchText} onInput={e => setSearchText((e.target as HTMLInputElement).value)} />

                <label className="cursor-pointer">
                    <input type="checkbox" className="hidden" checked={showUnprocessed} onChange={e => setShowUnprocessed((e.target as HTMLInputElement).checked)} />
                    <span className={`btn btn-sm ${showUnprocessed ? 'btn-accent' : 'btn-ghost border border-base-300'}`}>Не обработанные</span>
                </label>

                {hasEdits && <button className="btn btn-sm btn-success" onClick={handleSave}>Сохранить ({Object.keys(editedRows).length})</button>}
                <button className="btn btn-sm btn-ghost" onClick={handleSave}>Сохранить</button>
                <button className="btn btn-sm btn-accent" onClick={handleFinish}>Завершить отбор</button>
            </div>

            {/* Table */}
            <div className="flex-1 overflow-auto p-4"
                ref={(el: HTMLDivElement | null) => { (tableScrollRef as any).current = el; (virtualContainerRef as any).current = el; }}>
                {sortedSelections.length === 0 ? (
                    <div className="text-center py-16 text-base-content/60">Нет строк для отборки</div>
                ) : (
                    <table className="w-auto min-w-full border-collapse text-sm">
                        <thead>
                            <tr className="bg-base-200 text-base-content font-normal">
                                <th className="py-2 px-1.5 border border-base-300 w-12 text-center">
                                    <button className="btn btn-ghost btn-sm p-1 h-auto min-h-0"
                                        onClick={() => setIsColumnVisibilityModalOpen(true)} title="Настройка колонок">
                                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-base-content/70 hover:text-base-content" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                                            <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                        </svg>
                                    </button>
                                </th>
                                {filteredColumns.map(field => {
                                    const isDragging = draggedColumn === field;
                                    const isDragOver = dragOverColumn === field;
                                    return (
                                        <th key={field} {...headerProps(field)}
                                            className={`py-2 px-2 border border-base-300 relative cursor-move select-none font-normal ${isDragging ? 'opacity-50' : ''} ${isDragOver ? 'border-primary border-2 bg-primary/10' : ''}`}
                                            draggable={!isResizing}
                                            onDragStart={e => handleColumnDragStart(e, field)}
                                            onDragEnd={handleColumnDragEnd}
                                            onDragOver={e => handleColumnDragOver(e, field)}
                                            onDragLeave={handleColumnDragLeave}
                                            onDrop={e => handleColumnDrop(e, field)}
                                        >
                                            <div className="flex items-center gap-1.5 pr-3 pb-2">
                                                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 text-base-content/40 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 8h16M4 16h16" />
                                                </svg>
                                                <span className="text-xs">{SELECTION_LABELS[field]}</span>
                                                {sortConfig.field === field && <span className="text-xs text-primary">{sortConfig.direction === 'asc' ? '↑' : '↓'}</span>}
                                            </div>
                                            {field !== 'history' && (
                                                <div className="absolute bottom-0 right-0" style={{ marginBottom: '2px', marginRight: '2px' }}>
                                                    <ColumnFilter
                                                        field={field}
                                                        values={selections.map(s => getCellValue(s, field, {}))}
                                                        onFilterChange={handleFilterChange}
                                                        isOpen={openFilterField === field}
                                                        onToggle={() => setOpenFilterField(openFilterField === field ? null : field)}
                                                        sortConfig={sortConfig}
                                                        onSortChange={handleSortChange}
                                                        isNumberField={SELECTION_NUMBER_FIELDS.includes(field)}
                                                    />
                                                </div>
                                            )}
                                            <div data-resize-handle className="absolute right-0 top-0 h-full w-2 cursor-col-resize hover:bg-primary/20 z-10"
                                                style={{ marginRight: '-4px' }}
                                                onMouseDown={e => { e.stopPropagation(); setIsResizing(true); resizeHandleProps(field).onMouseDown(e as any); }} />
                                        </th>
                                    );
                                })}
                            </tr>
                        </thead>
                        <tbody>
                            {offsetY > 0 && <tr style={{ height: `${offsetY}px` }} />}
                            {visibleItems.map((sel, i) => {
                                const rowIndex = startIndex + i;
                                const coord = sel.coordinator;
                                const hasHistory = (sel.have_hst ?? 0) > 0;
                                const highlightClass = hasHistory ? 'bg-warning/10' : '';
                                const rowId = `sel-${sel.code}`;

                                return (
                                    <tr key={sel.code} className={`hover:bg-base-100 ${getRowClass(rowId)}`}>
                                        <td className="py-2 px-1.5 border border-base-300 text-center relative"
                                            onClick={() => {
                                                clearCellSelection(); setActiveCell(null);
                                                const data: Record<string, string> = {};
                                                filteredColumns.forEach(f => { data[f] = String(getCellValue(sel, f, editedRows)); });
                                                handleRowClick(rowId, data);
                                            }}>
                                            <svg xmlns="http://www.w3.org/2000/svg" className={`h-3 w-3 inline-block cursor-pointer transition-colors ${selectedRowId === rowId ? 'text-primary' : 'text-base-content/40 hover:text-base-content/60'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                                                <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                                            </svg>
                                            {selectedRowId === rowId && (
                                                <button className="absolute left-full top-1/2 -translate-y-1/2 ml-1 px-1.5 py-0.5 text-[10px] bg-primary text-primary-content rounded whitespace-nowrap z-20 shadow"
                                                    onClick={e => {
                                                        e.stopPropagation();
                                                        const data: Record<string, string> = {};
                                                        filteredColumns.forEach(f => { data[f] = String(getCellValue(sel, f, editedRows)); });
                                                        copyToClipboard(Object.values(data).join('\t'));
                                                    }}>
                                                    Копировать
                                                </button>
                                            )}
                                        </td>
                                        {filteredColumns.map((field, colIndex) => {
                                            const cellId = `${field}-${sel.code}`;
                                            const isActive = activeCell?.rowIndex === rowIndex && activeCell?.colIndex === colIndex;
                                            const cellValue = getCellValue(sel, field, editedRows);

                                            // ── ТГ: editable select ──
                                            if (field === 'ware_group') {
                                                return (
                                                    <td key={field} className={`py-1 px-1 border border-base-300 ${isActive ? 'ring-2 ring-primary' : ''}`}>
                                                        <LazySelect className="select select-xs select-bordered w-full bg-transparent"
                                                            options={wareCategories.map(c => ({ value: c.code, label: c.name }))}
                                                            value={coord?.ware_group_code ?? ''}
                                                            onChange={async v => {
                                                                const val = Number(v);
                                                                if (val && coord) {
                                                                    await dispatch(changeRowWareGroup({ code: offerCode, rowCode: coord.code, wareGroupCode: val }));
                                                                    dispatch(fetchBuyerSelections({ code: offerCode, buyerCodes: allBuyerCodes }));
                                                                }
                                                            }} />
                                                    </td>
                                                );
                                            }

                                            // ── Selection editable number fields ──
                                            if (field === 'sel_quantity' || field === 'sel_purchase_price' || field === 'sel_selling_price' || field === 'sel_recommended_price') {
                                                const selField = field.replace('sel_', '') as 'quantity' | 'purchase_price' | 'selling_price' | 'recommended_price';
                                                return (
                                                    <td key={field} className={`py-1 px-1 border border-base-300 ${highlightClass} ${isActive ? 'ring-2 ring-primary' : ''}`}>
                                                        <input type="number" className="input input-xs input-bordered w-full bg-transparent"
                                                            step={field === 'sel_quantity' ? '1' : '0.01'}
                                                            value={getEdited(sel, selField) ?? ''}
                                                            onChange={e => setFieldValue(sel.code, selField, Number((e.target as HTMLInputElement).value) || 0)} />
                                                    </td>
                                                );
                                            }

                                            // ── Selection status: editable select ──
                                            if (field === 'sel_status') {
                                                return (
                                                    <td key={field} className={`py-1 px-1 border border-base-300 ${isActive ? 'ring-2 ring-primary' : ''}`}>
                                                        <LazySelect className="select select-xs select-bordered w-full bg-transparent"
                                                            options={selectStatuses.map(st => ({ value: st.code, label: st.name }))}
                                                            value={getEdited(sel, 'status_code') ?? ''}
                                                            onChange={v => setFieldValue(sel.code, 'status_code', v)} />
                                                    </td>
                                                );
                                            }

                                            // ── History column ──
                                            if (field === 'history') {
                                                return (
                                                    <td key={field} className="py-2 px-2 border border-base-300 text-center">
                                                        {hasHistory && <BuyerSelectHistoryTooltip offerCode={offerCode} selectCode={sel.code} />}
                                                    </td>
                                                );
                                            }

                                            // ── Foto ──
                                            if (field === 'foto_url' && cellValue) {
                                                return (
                                                    <td key={field} className={`py-2 px-2 border border-base-300 ${isActive ? 'ring-2 ring-primary' : ''}`}>
                                                        <img src={cellValue} alt="" className="h-8 w-8 object-cover rounded" loading="lazy" />
                                                    </td>
                                                );
                                            }

                                            // ── Clickable article/brand for SR ──
                                            if (field === 'vendor_ware') {
                                                return (
                                                    <td key={field}
                                                        className={`py-2 px-2 border border-base-300 font-normal cursor-help ${getCellClass(cellId)} ${isActive ? 'ring-2 ring-primary' : ''}`}
                                                        style={{ minWidth: headerProps(field).style.minWidth }}
                                                        data-cell-id={cellId}
                                                        title="Клик для SR по артикулу"
                                                        onClick={e => {
                                                            e.stopPropagation(); clearRowSelection();
                                                            setActiveCell({ rowIndex, colIndex });
                                                            handleCellClick(cellId, String(cellValue));
                                                            if (coord?.vendor_ware) showSrArticle(coord.vendor_ware);
                                                        }}>
                                                        {cellValue || '\u2014'}
                                                    </td>
                                                );
                                            }
                                            if (field === 'vendor_brand') {
                                                return (
                                                    <td key={field}
                                                        className={`py-2 px-2 border border-base-300 font-normal cursor-help ${getCellClass(cellId)} ${isActive ? 'ring-2 ring-primary' : ''}`}
                                                        style={{ minWidth: headerProps(field).style.minWidth }}
                                                        data-cell-id={cellId}
                                                        title="Клик для SR по бренду+ТГ"
                                                        onClick={e => {
                                                            e.stopPropagation(); clearRowSelection();
                                                            setActiveCell({ rowIndex, colIndex });
                                                            handleCellClick(cellId, String(cellValue));
                                                            if (coord?.vendor_brand && coord?.ware_category_name) showSrBrandWareGroup(coord.vendor_brand, coord.ware_category_name);
                                                        }}>
                                                        {cellValue || '\u2014'}
                                                    </td>
                                                );
                                            }

                                            // ── Read-only cell ──
                                            return (
                                                <td key={field}
                                                    className={`py-2 px-2 border border-base-300 font-normal ${getCellClass(cellId)} ${isActive ? 'ring-2 ring-primary' : ''}`}
                                                    style={{ minWidth: headerProps(field).style.minWidth }}
                                                    data-cell-id={cellId}
                                                    onClick={e => { e.stopPropagation(); clearRowSelection(); setActiveCell({ rowIndex, colIndex }); handleCellClick(cellId, String(cellValue)); }}>
                                                    {field === 'vendor_description' ? (
                                                        <span className="truncate block max-w-[200px]" title={String(cellValue)}>{cellValue || '\u2014'}</span>
                                                    ) : cellValue || '\u2014'}
                                                </td>
                                            );
                                        })}
                                    </tr>
                                );
                            })}
                            {(totalHeight - offsetY - visibleItems.length * ROW_HEIGHT) > 0 && (
                                <tr style={{ height: `${totalHeight - offsetY - visibleItems.length * ROW_HEIGHT}px` }} />
                            )}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Modals */}
            <ConfirmModal
                open={!!confirmAction}
                message={confirmAction?.message || ''}
                onConfirm={() => confirmAction?.action()}
                onCancel={() => setConfirmAction(null)}
            />

            <ColumnVisibilityModal
                isOpen={isColumnVisibilityModalOpen}
                onClose={() => setIsColumnVisibilityModalOpen(false)}
                columns={SELECTION_FIELDS as string[]}
                columnLabels={SELECTION_LABELS as Record<string, string>}
                visibleColumns={Array.from(visibleColumns)}
                onToggleColumn={newVis => { setVisibleColumns(newVis); setColumnOrder(columnOrder.filter(c => newVis.has(c))); }}
            />

            {/* SR Popup */}
            {srPopup && (
                <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center" onClick={() => setSrPopup(null)}>
                    <div className="bg-base-100 rounded-lg shadow-xl max-w-2xl w-full max-h-[70vh] flex flex-col" onClick={e => e.stopPropagation()}>
                        <div className="p-4 border-b border-base-300 flex justify-between items-center">
                            <h3 className="font-semibold text-sm">{srPopup.title}</h3>
                            <button className="btn btn-ghost btn-xs" onClick={() => setSrPopup(null)}>{'\u2715'}</button>
                        </div>
                        <div className="flex-1 overflow-auto p-4">
                            {srPopup.loading ? (
                                <div className="text-center py-4"><span className="loading loading-spinner loading-sm" /></div>
                            ) : !srPopup.data || srPopup.data.length === 0 ? (
                                <div className="text-center py-4 text-base-content/60 text-sm">Нет данных</div>
                            ) : (
                                <table className="table table-xs w-full">
                                    <thead>
                                        <tr>
                                            {Object.keys(srPopup.data[0]).filter(k => k !== 'code').map(k => (
                                                <th key={k} className="text-xs">{k}</th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {srPopup.data.map((row: any, i: number) => (
                                            <tr key={i}>
                                                {Object.entries(row).filter(([k]) => k !== 'code').map(([k, v]) => (
                                                    <td key={k} className="text-xs">{v != null ? String(v) : '\u2014'}</td>
                                                ))}
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
});

export default SelectionDetailPage;
